import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';
import config from '../../config';
import { trackButtonClick } from '../../hooks/useTelemetry';
import { fieldError, indianMobile10 } from '../../lib/validation';

export default function CallbackForm() {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [vaccine, setVaccine] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const vaccineOptions = [
    'Flu Shot', 'Covid Booster', 'Tetanus', 'Hepatitis B',
    'Typhoid', 'Pneumococcal', 'HPV', 'Rabies', 'Other'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const err = fieldError({ name, phone });
    if (err) {
      setError(err);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${config.API}/callback-request`, {
        name, phone: indianMobile10(phone), vaccine_interest: vaccine, company_website: honeypot,
      });
      setSubmitted(true);
    } catch (err) {
      if (err?.response?.status === 429) {
        setError(err.response.data?.detail || 'Too many requests. Please try again later or contact us on WhatsApp.');
      } else {
        setError('Something went wrong. Please try WhatsApp or call us directly.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="callback-section" className="py-16 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="bg-gradient-to-br from-[#311b52] to-[#3e195e] rounded-3xl p-8 md:p-10 text-white relative overflow-hidden"
          >
            {/* Subtle decorative circle */}
            <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-[#673c96]/20 blur-2xl" />

            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#f37721]/20 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-[#f37721]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold" style={{ fontFamily: 'Poppins' }}>Request a Callback</h3>
                  <p className="text-xs text-white/60" style={{ fontFamily: 'Montserrat' }}>Not ready to download? We'll call you.</p>
                </div>
              </div>

              {submitted ? (
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-6">
                  <CheckCircle className="w-12 h-12 text-[#22c55e] mx-auto mb-3" />
                  <p className="text-base font-semibold mb-1" style={{ fontFamily: 'Poppins' }}>We've got your request!</p>
                  <p className="text-sm text-white/70" style={{ fontFamily: 'Montserrat' }}>
                    A healthcare coordinator will reach out within 15 minutes during business hours.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                  {error && <p className="text-xs text-red-300 bg-red-900/20 rounded-lg px-3 py-2">{error}</p>}

                  {/* Honeypot — must remain visually hidden and empty for humans */}
                  <input
                    type="text"
                    name="company_website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    aria-hidden="true"
                    style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      data-testid="callback-name"
                      value={name} onChange={e => setName(e.target.value)} required
                      placeholder="Your name"
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/15 text-white placeholder-white/40 text-sm focus:border-[#f37721]/50 focus:bg-white/15 outline-none transition-all"
                      style={{ fontFamily: 'Montserrat' }}
                    />
                    <input
                      data-testid="callback-phone"
                      value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} required maxLength={10}
                      placeholder="Phone number"
                      type="tel"
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/15 text-white placeholder-white/40 text-sm focus:border-[#f37721]/50 focus:bg-white/15 outline-none transition-all"
                      style={{ fontFamily: 'Montserrat' }}
                    />
                  </div>

                  <select
                    data-testid="callback-vaccine"
                    value={vaccine} onChange={e => setVaccine(e.target.value)} required
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/15 text-white text-sm focus:border-[#f37721]/50 outline-none transition-all appearance-none"
                    style={{ fontFamily: 'Montserrat' }}
                  >
                    <option value="" disabled className="text-[#311b52]">Vaccine of interest</option>
                    {vaccineOptions.map(v => <option key={v} value={v} className="text-[#311b52]">{v}</option>)}
                  </select>

                  <button
                    data-testid="callback-submit"
                    type="submit" disabled={loading}
                    onMouseDown={() => trackButtonClick('Request Callback', { vaccine: vaccine || 'unselected' })}
                    className="w-full sm:w-auto bg-[#f37721] hover:bg-[#d9651a] text-white rounded-full px-8 py-3 text-sm font-semibold transition-all hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {loading ? 'Submitting...' : <>Request Callback <ArrowRight className="w-4 h-4" /></>}
                  </button>

                  <p data-testid="callback-consent-microcopy"
                    className="text-[11px] leading-relaxed text-white/55 text-center sm:text-left pt-1"
                    style={{ fontFamily: 'Montserrat' }}>
                    By submitting, you agree that BookMyJab may contact you by phone, WhatsApp, SMS or email regarding your enquiry. See our{' '}
                    <Link to="/privacy-policy" className="underline hover:text-[#f37721] transition-colors">Privacy Policy</Link>.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
